// RELEASE-TENIS10 § 11 P106 — a child's place as support reads and corrects it: the city, its county and its country
// („Tecuci, Galați, Romania”). E11 holds: a city and its country, nothing more precise; the county only names the city among
// the others of the same name (1 435 names exist in several counties). The county shown is the one stored (the API's
// canonical ASCII name, its diacritics restored here) or, for a place just picked, the area Google gave it. The API resolves
// the stored county from that area itself (ADR 0002): the CPanel never sends a county of its own.

// The canonical county names (api common/countyRegistry) with their diacritics.
const COUNTY_LABELS = {
  Alba: "Alba", Arad: "Arad", Arges: "Argeș", Bacau: "Bacău", Bihor: "Bihor", "Bistrita-Nasaud": "Bistrița-Năsăud", Botosani: "Botoșani",
  Braila: "Brăila", Brasov: "Brașov", Bucuresti: "București", Buzau: "Buzău", Calarasi: "Călărași", "Caras-Severin": "Caraș-Severin",
  Cluj: "Cluj", Constanta: "Constanța", Covasna: "Covasna", Dambovita: "Dâmbovița", Dolj: "Dolj", Galati: "Galați", Giurgiu: "Giurgiu",
  Gorj: "Gorj", Harghita: "Harghita", Hunedoara: "Hunedoara", Ialomita: "Ialomița", Iasi: "Iași", Ilfov: "Ilfov", Maramures: "Maramureș",
  Mehedinti: "Mehedinți", Mures: "Mureș", Neamt: "Neamț", Olt: "Olt", Prahova: "Prahova", Salaj: "Sălaj", "Satu Mare": "Satu Mare",
  Sibiu: "Sibiu", Suceava: "Suceava", Teleorman: "Teleorman", Timis: "Timiș", Tulcea: "Tulcea", Valcea: "Vâlcea", Vaslui: "Vaslui",
  Vrancea: "Vrancea",
}

const text = value => (typeof value === "string" ? value.trim() : "")
const fold = value => text(value).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
const labelByFold = new Map(Object.entries(COUNTY_LABELS).flatMap(([key, label]) => [[fold(key), label], [fold(label), label]]))
// „Galați County”, „Județul Galați”, „Plovdiv Province” name the county „Galați” / „Plovdiv”
const withoutDesignator = name => name.replace(/^jude[țţt]ul\s+/i, "").replace(/\s+(county|province|oblast)$/i, "").trim()
const countyLabel = value => labelByFold.get(fold(value)) || text(value)

/** The county as support reads it: the stored one, else the picked place's area; null when neither is known. */
export const countyOfPlace = place => {
  if (text(place?.county)) return countyLabel(place.county)
  const area = text(place?.administrativeArea?.longName) || text(place?.administrativeArea?.shortName)
  return area ? countyLabel(withoutDesignator(area)) : null
}

/** „Tecuci, Galați, Romania”: the county left out when unknown or when it is the city itself (Bucharest). */
export const placeText = place => {
  const city = text(place?.city)
  if (!city) return ""
  const county = countyOfPlace(place)
  return [city, county && fold(county) !== fold(city) ? county : null, text(place?.country)].filter(Boolean).join(", ")
}

/** The child's place from a Geocoder result: the city, its country and the area the API names the county from — no coordinates (E11). */
export const childPlaceOf = result => {
  const component = type => result?.address_components?.find(item => item.types?.includes(type))
  const city = component("locality")?.long_name || component("postal_town")?.long_name || component("administrative_area_level_2")?.long_name
  const country = component("country")
  if (!city || !country) throw new Error("This city could not be verified.")
  const area = component("administrative_area_level_1")
  return { city, country: country.long_name, countryCode: country.short_name.toUpperCase(),
    ...(area ? { administrativeArea: { shortName: area.short_name, longName: area.long_name } } : {}) }
}
